import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { IconRotate2, IconRotateClockwise2 } from '@tabler/icons-react';
import {
  Button,
  Group,
  IconButton,
  Modal,
  Stack,
  Text,
  WP_ICON_NAV,
} from '@wapharma/design-system';
import { avatarFileFromBlob, getCroppedImage } from '../lib/cropImageToBlob';
import styles from './AvatarCropModal.module.css';

type Props = {
  opened: boolean;
  imageSrc: string | null;
  onClose: () => void;
  onConfirm: (file: File) => void;
};

export function AvatarCropModal({ opened, imageSrc, onClose, onConfirm }: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const cropPixelsRef = useRef<Area | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened || !imageSrc) return;
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    cropPixelsRef.current = null;
    setPending(false);
    setError(null);
  }, [opened, imageSrc]);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    cropPixelsRef.current = pixels;
  }, []);

  const handleConfirm = () => {
    const pixels = cropPixelsRef.current;
    if (!imageSrc || pending) return;
    if (!pixels) {
      setError('Cadre pas prêt — bougez légèrement l’image.');
      return;
    }
    setPending(true);
    setError(null);
    void getCroppedImage(imageSrc, pixels, rotation)
      .then((blob) => {
        onConfirm(avatarFileFromBlob(blob));
        onClose();
      })
      .catch((err: unknown) => {
        console.error('[AvatarCropModal]', err);
        setError('Impossible de cadrer l’image. Réessayez.');
      })
      .finally(() => setPending(false));
  };

  return (
    <Modal
      opened={opened && Boolean(imageSrc)}
      onClose={onClose}
      title="Cadrer la photo"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={pending} loading={pending}>
            Appliquer
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <div className={styles.cropStage}>
          {imageSrc ? (
            <Cropper
              key={imageSrc}
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              showGrid
            />
          ) : null}
        </div>

        <label className={styles.control}>
          <Text size="sm" c="secondary">
            Zoom
          </Text>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.currentTarget.value))}
            className={styles.slider}
            aria-label="Zoom"
          />
        </label>

        <Group gap="sm" align="center" justify="space-between">
          <Text size="sm" c="secondary">
            Rotation
          </Text>
          <Group gap="xs" align="center">
            <IconButton
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Tourner à gauche"
              onClick={() => setRotation((r) => (r - 90 + 360) % 360)}
            >
              <IconRotate2 {...WP_ICON_NAV} aria-hidden />
            </IconButton>
            <IconButton
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Tourner à droite"
              onClick={() => setRotation((r) => (r + 90) % 360)}
            >
              <IconRotateClockwise2 {...WP_ICON_NAV} aria-hidden />
            </IconButton>
          </Group>
        </Group>

        {error ? (
          <Text size="sm" c="error">
            {error}
          </Text>
        ) : null}
      </Stack>
    </Modal>
  );
}
